"use client";

import { useState, useRef, useCallback } from "react";
import { ArrowUp } from "lucide-react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { AuthModal } from "@/components/auth/auth-modal";
import { cn } from "@/lib/utils";

interface PostReplayInputProps {
  replayQuery?: string;
}

export function PostReplayInput({ replayQuery }: PostReplayInputProps) {
  const router = useRouter();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [draft, setDraft] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(() => {
    const text = draft.trim() || replayQuery || "";
    if (!text) return;

    if (!user) {
      if (isMobile) {
        router.push("/signup");
      } else {
        setShowAuthModal(true);
      }
      return;
    }

    router.push(`/?prefill=${encodeURIComponent(text)}`);
  }, [draft, replayQuery, user, isMobile, router]);

  return (
    <>
      <div className="relative w-full rounded-2xl bg-zinc-900/80 border border-white/[0.06] overflow-hidden transition-colors focus-within:border-white/[0.12]">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            // Auto-resize
            e.target.style.height = "auto";
            e.target.style.height = `${e.target.scrollHeight}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder={replayQuery ? `Try something like "${replayQuery.slice(0, 60)}${replayQuery.length > 60 ? "…" : ""}"` : "Ask Xynth anything…"}
          rows={1}
          className={cn(
            "w-full resize-none bg-transparent text-sm text-white placeholder:text-zinc-500",
            "px-4 pt-3 pb-10 min-h-[44px] max-h-[120px]",
            "outline-none border-none focus:ring-0",
          )}
        />
        <div className="absolute bottom-2 right-2">
          <button
            onClick={handleSubmit}
            className={cn(
              "flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200",
              draft.trim()
                ? "bg-white text-black hover:bg-zinc-200"
                : "bg-white/[0.08] text-zinc-500 hover:bg-white/[0.12] hover:text-zinc-300"
            )}
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>

      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}
